import { useCallback, useMemo, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconPrinter } from "@tabler/icons-react";
import { useBridgeStatus } from "../../hooks/useBridgeStatus";
import { useWorkstationDeviceStatus } from "../../hooks/useWorkstationDeviceStatus";
import { WorkstationSelectorModal } from "../bridge";

interface StickerPrinterStatusPanelProps {
  title?: string;
}

/**
 * Returns true when the sticker printer workstation is online and the
 * Dymo device is connected. Co-located here because it is derived
 * directly from StickerPrinterStatusPanel's status; it is a plain hook,
 * not a React component, so this does not violate the one-component-per-file rule.
 */
export function useStickerPrinterReady(): boolean {
  const status = useWorkstationDeviceStatus("sticker_printer");
  return status.deviceConnected && status.workstationOnline;
}

/**
 * Printer status panel for Dymo sticker printing via Bridge.
 * Checks real backend workstation + device connection status.
 * Clicking opens the workstation selector if not configured or offline.
 */
export default function StickerPrinterStatusPanel({
  title = "Sticker Printer (Dymo)",
}: StickerPrinterStatusPanelProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  // Keep bridge WebSocket alive while this panel is mounted so
  // the local bridge connection persists across page navigations.
  useBridgeStatus();
  const status = useWorkstationDeviceStatus("sticker_printer");

  const statusColor = useMemo((): string => {
    if (!status.hasWorkstation) return "var(--mantine-color-orange-6)";
    if (status.isLoading) return "var(--mantine-color-gray-5)";
    if (!status.workstationOnline) return "var(--mantine-color-red-6)";
    if (!status.device) return "var(--mantine-color-orange-6)";
    if (!status.deviceConnected) return "var(--mantine-color-red-6)";
    return "var(--mantine-color-green-6)";
  }, [
    status.hasWorkstation,
    status.isLoading,
    status.workstationOnline,
    status.device,
    status.deviceConnected,
  ]);

  const statusMessage = useMemo((): string => {
    if (!status.hasWorkstation) return "Click to configure your workstation";
    if (status.isLoading) return "Checking connection status…";
    if (!status.workstationOnline)
      return `${status.workstationName || "Workstation"} is offline`;
    if (!status.device) return "No Dymo printer on this workstation";
    if (!status.deviceConnected) return `${status.device.name} is disconnected`;
    return `${status.device.name} on ${status.workstationName}`;
  }, [
    status.hasWorkstation,
    status.isLoading,
    status.workstationOnline,
    status.workstationName,
    status.device,
    status.deviceConnected,
  ]);

  const handleOpen = useCallback(() => {
    setSelectorOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setSelectorOpen(false);
  }, []);

  return (
    <>
      <UnstyledButton
        onClick={handleOpen}
        style={{ cursor: "pointer", width: "100%" }}
      >
        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              gap="xs"
            >
              <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                <IconPrinter size={16} style={{ flexShrink: 0 }} />
                <Text fw={500} size="sm" truncate>
                  {title}
                </Text>
              </Group>
              <Tooltip label={statusMessage} withArrow withinPortal>
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: statusColor,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            </Group>
            <Text size="xs" c="dimmed" truncate>
              {statusMessage}
            </Text>
          </Stack>
        </Paper>
      </UnstyledButton>

      <WorkstationSelectorModal opened={selectorOpen} onClose={handleClose} />
    </>
  );
}
